import {SEASONS} from '@generic/data/assumptions';
import getMarketRateElements from './shared/getMarketRateElements';
import sharedConEdStaticRateElements from './shared/sharedConEdStaticRateElements';
import {
  MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_NOT_WESTCHESTER,
  MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_WESTCHESTER,
} from './constants';
import getSC9VDERRateElement from './shared/getSC9VDERRateElement';

// Service Classification ("SC") 9
// Rate 4
const RATE_NAME = 'sc9Rate4';

/**
 * MSC - Market Supply Charge
 * Calculated using ConEd's Market Supply Charge Calculator here:
 * https://www.coned.com/en/accounts-billing/your-bill/rate-calculators/market-supply-charge
 *
 * SC9 rate is equivalent to EL9 - General - Large Commercial Rate. Different name, same thing.
 * Rates I, III and IV are the same.
 * Rates II and V are the same.
 *
 * for EL9 I-V, for the dates Jan 1, 2024 - Dec 31, 2024 are shown as on peak/off peak/standard.
 * April 10, 2025:
 * 1. Do NOT use high/low tension. No impact on rates.
 * 2. Use the standard charge.
 * https://twist.com/a/9369/ch/691652/t/6861262/c/94867920
 */
const MSC_ENERGY_CHARGES = {
  H: 0.065397,
  I: 0.065798,
  J: 0.067563,
};

// Page 663-666 - ./docs/coned-tariff-rate-book-2025.pdf
// PDF from - https://lite.coned.com/_external/cerates/documents/elecPSC10/electric-tariff.pdf
const DEMAND_DELIVERY_2025 = {
  EXISTING_CUSTOMRS_PHASE_IN: {
    FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS: 123.71,
    EVERYONE: {
      LOW_TENSION: {
        ContractDemand_DollarsPerKw: 11.53,
        DailyDemand_Summer_Monday_Friday_8AM_6PM_DollarsPerKw: 0.7139,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 1.6403,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 1.0631,
      },
      HIGH_TENSION: {
        ContractDemand_DollarsPerKw: 5.68,
        DailyDemand_Summer_Monday_Friday_8AM_6PM_DollarsPerKw: 0.7169,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 0.6396,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 0.6694,
      },
    },
    WHOLESALE_GENERATORS: {
      LOW_TENSION: {
        ContractDemand_DollarsPerKw: 11.53,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 1.6403,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 0.7557,
      },
      HIGH_TENSION: {
        ContractDemand_DollarsPerKw: 5.68,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 0.6396,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 0.3588,
      },
    },
  },
  ALL_OTHER_CUSTOMERS: {
    // Page 665
    FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS: 71.0,
    EVERYONE: {
      LOW_TENSION: {
        ContractDemand_DollarsPerKw: 9.18,
        DailyDemand_Summer_Monday_Friday_8AM_6PM_DollarsPerKw: 0.6671,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 2.2761,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 1.1744,
      },
      HIGH_TENSION: {
        ContractDemand_DollarsPerKw: 5.68,
        DailyDemand_Summer_Monday_Friday_8AM_6PM_DollarsPerKw: 0.83,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 0.6701,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 0.8663,
      },
    },
    WHOLESALE_GENERATORS: {
      LOW_TENSION: {
        ContractDemand_DollarsPerKw: 9.18,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 2.2761,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 0.9144,
      },
      HIGH_TENSION: {
        ContractDemand_DollarsPerKw: 0.83,
        DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw: 1.1038,
        DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw: 0.6033,
      },
    },
  },
};

// Demand
// Rates taken from PDF located at ./docs/2025-MSC-Capacity-Charge-SC9-Rates-(Demand).pdf
// PDF saved from - https://lite.coned.com/_external/cerates/documents/elecPSC10/StatMSCCAP-120.pdf
const MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_WESTCHESTER = 2.84;
const MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_NOT_WESTCHESTER = 8.92;
const getMscCapacityCharge = ({isOnMarketBasedRate, isLocatedInWestchester}) => {
  if (isOnMarketBasedRate && isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_WESTCHESTER;
  }
  if (isOnMarketBasedRate && !isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_NOT_WESTCHESTER;
  }
  if (!isOnMarketBasedRate && isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_WESTCHESTER;
  }
  if (!isOnMarketBasedRate && !isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_NOT_WESTCHESTER;
  }
  return 0;
};

const sc9Rate4 = ({zone, isHighTension, isOnMarketBasedRate, isLocatedInWestchester}) => ({
  name: RATE_NAME,
  title: 'SC 9 Rate IV',
  maxKw: 1500,
  rateElements: [
    ...sharedConEdStaticRateElements,
    ...getMarketRateElements(zone, isOnMarketBasedRate),
    ...getSC9VDERRateElement({isHighTension, isLocatedInWestchester, isOnMarketBasedRate}).Standby,
    {
      rateElementType: 'MonthlyDemand',
      name: 'MSC Capacity Charge',
      billingCategory: 'supply',
      rateComponents: [
        // Rates taken from PDF located at ./docs/2025-MSC-Capacity-Charge-SC9-Rates-(Demand).pdf
        // PDF saved from - https://lite.coned.com/_external/cerates/documents/elecPSC10/StatMSCCAP-120.pdf
        {
          charge: getMscCapacityCharge({isOnMarketBasedRate, isLocatedInWestchester}),
          name: 'MSC Capacity Charge',
        },
      ],
    },
    {
      rateElementType: 'MonthlyEnergy',
      name: 'MSC Energy Charge',
      billingCategory: 'supply',
      rateComponents: [
        {
          charge: isOnMarketBasedRate ? 0 : MSC_ENERGY_CHARGES[zone],
          name: 'MSC Energy Charge',
        },
      ],
    },
    {
      rateElementType: 'FixedPerMonth',
      name: 'Customer Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge:
            DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS,
          name: 'Customer Charge',
        },
      ],
    },
    // https://lite.coned.com/_external/cerates/elec_dlm.asp
    // ./docs/april-2025-rate-update/june-2024-dynamic-load-management-surcharge.pdf
    {
      rateElementType: 'AnnualDemand',
      name: 'Dynamic Load Management ("DLM") Surcharge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: 0.22,
          name: 'CSRP & Term-/Auto-DLM Component',
        },
        {
          charge: 0.13,
          name: 'DLC & DLRP Component',
        },
      ],
    },
    {
      rateElementType: 'AnnualDemand',
      name: 'Contract Demand Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: isHighTension
            ? DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.HIGH_TENSION
                .ContractDemand_DollarsPerKw
            : DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.LOW_TENSION
                .ContractDemand_DollarsPerKw,
          name: 'All Hours',
        },
      ],
    },
    {
      rateElementType: 'DemandPerDay',
      name: 'As-Used Demand Charge',
      billingCategory: 'delivery',
      rateComponents: [
        // ConEd does not describe all hours for demands, which may trigger warnings in console
        {
          charge: isHighTension
            ? DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.HIGH_TENSION
                .DailyDemand_Summer_Monday_Friday_8AM_6PM_DollarsPerKw
            : DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.LOW_TENSION
                .DailyDemand_Summer_Monday_Friday_8AM_6PM_DollarsPerKw,
          months: SEASONS.map((season, i) => (season === 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17], // 8am to 6pm
          name: 'Summer - 8am to 6p',
        },
        {
          charge: isHighTension
            ? DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.HIGH_TENSION
                .DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw
            : DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.LOW_TENSION
                .DailyDemand_Summer_Monday_Friday_8AM_10PM_DollarsPerKw,
          months: SEASONS.map((season, i) => (season === 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], // 8am to 10pm
          name: 'Summer - 8am to 10p',
        },
        {
          charge: isHighTension
            ? DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.HIGH_TENSION
                .DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw
            : DEMAND_DELIVERY_2025.EXISTING_CUSTOMRS_PHASE_IN.EVERYONE.LOW_TENSION
                .DailyDemand_Other_Monday_Friday_8AM_10PM_DollarsPerKw,
          months: SEASONS.map((season, i) => (season !== 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], // 8am to 10pm
          name: 'Winter - 8am to 10p',
        },
      ],
    },
    // Value Distributed Energy Resources
    // ./docs/april-2025-rate-update/april-2025-vder.pdf
    // PDF from - https://lite.coned.com/_external/cerates/elec_VDER_CRstatement_PSC10.asp
    {
      rateElementType: 'MonthlyDemand',
      name: 'VDER Cost Recovery Charge - Demand',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: isHighTension ? -0.01 : 0,
          name: 'VDER Cost Recovery Charge - Demand',
        },
      ],
    },
    {
      id: 'vderCostRecoveryCharge',
      rateElementType: 'MonthlyEnergy',
      name: 'VDER Cost Recovery Charge - Energy',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: 0,
          name: 'VDER Cost Recovery Charge - Energy',
        },
      ],
    },
    // https://lite.coned.com/_external/cerates/elec_sdr.asp
    // ./docs/april-2025-rate-update/march-2025-delivery-revenue-surcharge.pdf
    {
      id: 'deliveryRevenueSurcharge',
      rateElementType: 'MonthlyDemand',
      name: 'Delivery Revenue Surcharge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: -1.228632,
          name: 'Delivery Revenue Surcharge',
        },
      ],
    },
    // ./docs/april-2025-rate-update/msc-adjustment-factor.pdf
    // PDF from - https://lite.coned.com/_external/cerates/PSC10-msc-adjustments.asp
    // Non-residential Use
    {
      id: 'mscAdjustmentFactor',
      rateElementType: 'MonthlyEnergy',
      name: 'MSC Adjustment Factor',
      billingCategory: 'supply',
      rateComponents: [
        {
          charge: isLocatedInWestchester ? 0.003734 : -0.004456,
          name: 'MSC Adjustment Factor',
        },
      ],
    },
  ],
});

export default sc9Rate4;
